import React, {
  useState,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { Modal } from "@/components/Modal.jsx";
import { ShoppingCart, ExternalLink, AlertTriangle, Wallet, CreditCard } from "lucide-react";
import { useAuthContext } from "@/wrappers/AuthWrapper.jsx";
import { useSkinsMasterContext } from "./SkinsMaster.jsx";
import { getv, trim2, tofeth, nils, toeth } from "@/utils/utils.jsx";
import _ from "lodash";
import DNA_SkinsStore from "@/contracts/v2/DNA_SkinsStore.js";
import ERC20base from "@/contracts/v2/ERC20base.js";
import { contractAddress_list } from "@/contracts/contracts.jsx";
import { polytxnlink } from "@/queries/queries.jsx";
import { TokenIcon } from "../../components/utilityComps.jsx";

const PurchaseContext = createContext({});
export const usePurchaseContext = () => useContext(PurchaseContext);

const PurchaseButton = ({ item_type = "skin" }) => {
  return (
    <button className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-r3reg to-r3dark hover:from-r3dark hover:to-r3reg text-white rounded-lg font-medium transition-all duration-200 shadow-lg">
      <ShoppingCart className="w-4 h-4" />
      <span>Buy {item_type === "pack" ? "Pack" : "Skin"}</span>
    </button>
  );
};

// Stage 1: Authentication Check
const AuthenticationCheck = () => {
  const { set_next_allowed, set_step, temp, set_temp } = usePurchaseContext();
  const aucon = useAuthContext();

  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    try {
      set_temp((prev) => ({ ...prev, status: "checking", error: "" }));

      if (!aucon.auth || !aucon.vault) {
        throw new Error("Please connect your wallet to continue");
      }

      // Verify wallet connection is active
      const ciparams = await aucon.get_contract_init_params();
      if (!ciparams) {
        throw new Error("Wallet connection not ready");
      }

      set_temp((prev) => ({ ...prev, status: "success" }));
      set_next_allowed(true);

      // Auto-advance after 2 seconds
      setTimeout(() => {
        set_step(2);
      }, 2000);
    } catch (error) {
      console.error("Authentication check failed:", error);
      set_temp((prev) => ({
        ...prev,
        status: "error",
        error: error.message || "Authentication failed",
      }));
    }
  };

  return (
    <div className="p-6">
      <h3 className="text-xl font-semibold text-white mb-4 text-center">
        Authentication Check
      </h3>

      <div className="max-w-md mx-auto">
        {temp.status === "checking" && (
          <div className="space-y-4 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="text-gray-300">Verifying wallet connection...</p>
            <p className="text-gray-400 text-sm">Checking authentication status</p>
          </div>
        )}

        {temp.status === "success" && (
          <div className="space-y-4 text-center">
            <div className="bg-green-500 rounded-full p-2 w-12 h-12 mx-auto flex items-center justify-center">
              <span className="text-white font-bold">✓</span>
            </div>
            <p className="text-green-400 font-semibold">Wallet Connected!</p>
            <p className="text-gray-400 text-sm">
              Authentication verified. Proceeding to payment options...
            </p>
          </div>
        )}

        {temp.status === "error" && (
          <div className="space-y-4 text-center">
            <div className="bg-red-500 rounded-full p-2 w-12 h-12 mx-auto flex items-center justify-center">
              <span className="text-white font-bold">!</span>
            </div>
            <p className="text-red-400 font-semibold">Authentication Failed</p>
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
              <p className="text-red-300 text-sm">{temp.error}</p>
            </div>
            <button
              onClick={checkAuthentication}
              className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-6 py-2 rounded-lg transition-colors"
            >
              Retry Connection
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Stage 2: Token Selection & Balance Display
const TokenSelection = () => {
  const {
    item,
    item_type,
    set_next_allowed,
    set_step,
    temp,
    set_temp,
    selected_token,
    set_selected_token,
  } = usePurchaseContext();
  const aucon = useAuthContext();
  const skm_con = useSkinsMasterContext();

  const [token_balances, set_token_balances] = useState({});
  const [loading_balances, set_loading_balances] = useState(true);

  const available_tokens = ["SGC", "DEZ", "WETH"];

  useEffect(() => {
    fetchBalances();
  }, []);

  useEffect(() => {
    if (selected_token) {
      set_next_allowed(true);
    } else {
      set_next_allowed(false);
    }
  }, [selected_token]);

  const fetchBalances = async () => {
    try {
      set_loading_balances(true);
      const ciparams = await aucon.get_contract_init_params();
      const balances = {};

      for (const token of available_tokens) {
        try {
          const token_contract = await ERC20base.get_contract(token, ciparams);
          const balance_wei = await token_contract.balanceOf(aucon.vault);
          const balance_formatted = parseFloat(tofeth(balance_wei));
          balances[token] = balance_formatted;
        } catch (error) {
          console.error(`Failed to fetch ${token} balance:`, error);
          balances[token] = 0;
        }
      }

      set_token_balances(balances);
      set_loading_balances(false);
    } catch (error) {
      console.error("Failed to fetch balances:", error);
      set_loading_balances(false);
    }
  };

  const get_token_price = (token) => {
    if (token === "SGC") {
      return item_type === "pack" ? item.price_sgc : item.price_sgc;
    } else {
      // DEZ and WETH use USD pricing
      const usd_price = item_type === "pack" ? item.price_usd : item.price_usd;
      const token_amount = skm_con.usd_to_tok_val(usd_price, token);
      return token_amount || 0;
    }
  };

  const get_usd_value = (token) => {
    if (token === "SGC") {
      // SGC doesn't have USD conversion in the current system
      return null;
    } else {
      return item_type === "pack" ? item.price_usd : item.price_usd;
    }
  };

  const is_affordable = (token) => {
    const required = get_token_price(token);
    const balance = token_balances[token] || 0;
    return balance >= required;
  };

  const handleTokenSelect = (token) => {
    set_selected_token(token);
    set_temp((prev) => ({
      ...prev,
      selected_token: token,
      token_price: get_token_price(token),
      usd_value: get_usd_value(token),
    }));
  };

  return (
    <div className="p-6">
      <h3 className="text-xl font-semibold text-white mb-4 text-center">
        Select Payment Method
      </h3>

      <div className="max-w-md mx-auto space-y-6">
        {/* Item Summary */}
        <div className="bg-slate-600/20 border border-slate-500/30 rounded-lg p-4 backdrop-blur-md">
          <h4 className="text-white font-medium mb-2">
            {item_type === "pack" ? "Pack" : "Skin"}: {item?.name || "Unknown"}
          </h4>
          {item_type === "pack" && (
            <p className="text-gray-400 text-sm">
              Contains {item?.skinindi_ids?.length || 0} skins
            </p>
          )}
        </div>

        {loading_balances ? (
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-300">Loading token balances...</p>
          </div>
        ) : (
          <div className="space-y-3">
            <h4 className="text-white font-medium mb-3">Available Payment Options</h4>

            {available_tokens.map((token) => {
              const token_price = get_token_price(token);
              const usd_value = get_usd_value(token);
              const balance = token_balances[token] || 0;
              const affordable = is_affordable(token);
              const is_selected = selected_token === token;

              return (
                <button
                  key={token}
                  onClick={() => handleTokenSelect(token)}
                  disabled={!affordable}
                  className={`w-full p-4 rounded-lg border transition-all duration-200 text-left ${
                    is_selected
                      ? "border-blue-500 bg-blue-500/20"
                      : affordable
                        ? "border-slate-500/50 bg-slate-600/20 hover:border-slate-400/50 hover:bg-slate-600/30"
                        : "border-red-500/50 bg-red-500/10 opacity-50 cursor-not-allowed"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <TokenIcon token={token} size="md" />
                      <div>
                        <div className="text-white font-medium">{token}</div>
                        <div className="text-gray-400 text-sm">
                          Balance: {balance.toFixed(token === "WETH" ? 4 : 2)} {token}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-bold ${affordable ? "text-white" : "text-red-400"}`}>
                        {token_price.toFixed(token === "WETH" ? 4 : 2)} {token}
                      </div>
                      {usd_value && (
                        <div className="text-gray-400 text-sm">
                          ≈ ${usd_value.toFixed(2)} USD
                        </div>
                      )}
                      {!affordable && (
                        <div className="text-red-400 text-xs mt-1">
                          Insufficient balance
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {selected_token && (
          <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <CreditCard className="w-4 h-4 text-blue-400" />
              <span className="text-blue-400 font-medium">Selected Payment Method</span>
            </div>
            <div className="text-white">
              {get_token_price(selected_token).toFixed(selected_token === "WETH" ? 4 : 2)} {selected_token}
              {get_usd_value(selected_token) && (
                <span className="text-gray-400 ml-2">
                  (≈ ${get_usd_value(selected_token).toFixed(2)} USD)
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Stage 3: Balance & Allowance Validation
const BalanceAllowanceValidation = () => {
  const {
    item,
    item_type,
    selected_token,
    temp,
    set_temp,
    set_next_allowed,
    set_step,
  } = usePurchaseContext();
  const aucon = useAuthContext();
  const skm_con = useSkinsMasterContext();

  const [validation_status, set_validation_status] = useState("checking");
  const [token_balance, set_token_balance] = useState(0);
  const [token_allowance, set_token_allowance] = useState(0);
  const [required_amount, set_required_amount] = useState(0);
  const [allowance_amount, set_allowance_amount] = useState("");
  const [approving, set_approving] = useState(false);

  const payrouter_address = contractAddress_list.dnapayrouter;

  useEffect(() => {
    if (selected_token) {
      checkBalanceAndAllowance();
    }
  }, [selected_token]);

  const checkBalanceAndAllowance = async () => {
    try {
      set_validation_status("checking");
      set_temp((prev) => ({ ...prev, validation_error: "" }));

      const ciparams = await aucon.get_contract_init_params();
      const token_contract = await ERC20base.get_contract(selected_token, ciparams);

      // Calculate required amount
      let required;
      if (selected_token === "SGC") {
        required = item_type === "pack" ? item.price_sgc : item.price_sgc;
      } else {
        const usd_price = item_type === "pack" ? item.price_usd : item.price_usd;
        required = skm_con.usd_to_tok_val(usd_price, selected_token) || 0;
      }
      set_required_amount(required);

      // Get current balance and allowance
      const [balance_wei, allowance_wei] = await Promise.all([
        token_contract.balanceOf(aucon.vault),
        token_contract.allowance(aucon.vault, payrouter_address),
      ]);

      const balance = parseFloat(tofeth(balance_wei));
      const allowance = parseFloat(tofeth(allowance_wei));

      set_token_balance(balance);
      set_token_allowance(allowance);

      // Check balance first
      if (balance < required) {
        set_validation_status("insufficient_balance");
        set_temp((prev) => ({
          ...prev,
          validation_error: `Insufficient ${selected_token} balance. Need ${required.toFixed(selected_token === "WETH" ? 4 : 2)} more.`,
        }));
        return;
      }

      // Check allowance
      if (allowance < required) {
        set_validation_status("insufficient_allowance");
        // Set suggested allowance with $2 buffer
        const buffer = selected_token === "SGC" ? 2 : skm_con.usd_to_tok_val(2, selected_token) || 0;
        const suggested = Math.min(required + buffer, balance);
        set_allowance_amount(suggested.toFixed(selected_token === "WETH" ? 4 : 2));
        return;
      }

      // All checks passed
      set_validation_status("sufficient");
      set_next_allowed(true);

      // Auto-advance after 2 seconds
      setTimeout(() => {
        set_step(4);
      }, 2000);
    } catch (error) {
      console.error("Balance/Allowance validation failed:", error);
      set_validation_status("error");
      set_temp((prev) => ({
        ...prev,
        validation_error: error.message || "Validation failed",
      }));
    }
  };

  const handleApproval = async () => {
    try {
      set_approving(true);
      const ciparams = await aucon.get_contract_init_params();
      const token_contract = await ERC20base.get_contract(selected_token, ciparams);

      const approval_amount = toeth(parseFloat(allowance_amount));
      await token_contract.approve(payrouter_address, approval_amount);

      // Wait a bit for blockchain propagation
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Recheck validation
      await checkBalanceAndAllowance();
    } catch (error) {
      console.error("Approval failed:", error);
      set_temp((prev) => ({
        ...prev,
        validation_error: error.message || "Approval failed",
      }));
    } finally {
      set_approving(false);
    }
  };

  return (
    <div className="p-6">
      <h3 className="text-xl font-semibold text-white mb-4 text-center">
        {selected_token} Balance & Allowance Check
      </h3>

      <div className="max-w-md mx-auto">
        {validation_status === "checking" && (
          <div className="space-y-4 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="text-gray-300">Checking {selected_token} requirements...</p>
            <p className="text-gray-400 text-sm">
              Required: {required_amount.toFixed(selected_token === "WETH" ? 4 : 2)} {selected_token}
            </p>
          </div>
        )}

        {validation_status === "sufficient" && (
          <div className="space-y-4 text-center">
            <div className="bg-green-500 rounded-full p-2 w-12 h-12 mx-auto flex items-center justify-center">
              <span className="text-white font-bold">✓</span>
            </div>
            <p className="text-green-400 font-semibold">All Requirements Met!</p>
            <p className="text-gray-400 text-sm">
              You have sufficient {selected_token} balance and allowance. Proceeding to summary...
            </p>
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Balance:</span>
                  <span className="text-white">{token_balance.toFixed(selected_token === "WETH" ? 4 : 2)} {selected_token}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Allowance:</span>
                  <span className="text-white">{token_allowance.toFixed(selected_token === "WETH" ? 4 : 2)} {selected_token}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Required:</span>
                  <span className="text-white">{required_amount.toFixed(selected_token === "WETH" ? 4 : 2)} {selected_token}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {validation_status === "insufficient_balance" && (
          <div className="space-y-4 text-center">
            <div className="bg-red-500 rounded-full p-2 w-12 h-12 mx-auto flex items-center justify-center">
              <span className="text-white font-bold">!</span>
            </div>
            <p className="text-red-400 font-semibold">Insufficient {selected_token} Balance</p>
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
              <p className="text-red-300 text-sm">{temp.validation_error}</p>
            </div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Your Balance:</span>
                <span className="text-white">{token_balance.toFixed(selected_token === "WETH" ? 4 : 2)} {selected_token}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Required:</span>
                <span className="text-white">{required_amount.toFixed(selected_token === "WETH" ? 4 : 2)} {selected_token}</span>
              </div>
            </div>
          </div>
        )}

        {validation_status === "insufficient_allowance" && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="bg-orange-500 rounded-full p-2 w-12 h-12 mx-auto mb-4">
                <AlertTriangle className="w-6 h-6 text-white mx-auto" />
              </div>
              <p className="text-orange-400 font-semibold">Allowance Required</p>
              <p className="text-gray-400 text-sm">
                You need to approve {selected_token} spending for the purchase
              </p>
            </div>

            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Current Allowance:</span>
                  <span className="text-white">{token_allowance.toFixed(selected_token === "WETH" ? 4 : 2)} {selected_token}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Required:</span>
                  <span className="text-white">{required_amount.toFixed(selected_token === "WETH" ? 4 : 2)} {selected_token}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Your Balance:</span>
                  <span className="text-white">{token_balance.toFixed(selected_token === "WETH" ? 4 : 2)} {selected_token}</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {selected_token} Allowance Amount
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={allowance_amount}
                    onChange={(e) => set_allowance_amount(e.target.value)}
                    className="w-full px-4 py-3 pr-20 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={`Enter ${selected_token} allowance`}
                    step={selected_token === "WETH" ? "0.0001" : "0.01"}
                  />
                  <button
                    type="button"
                    onClick={() => set_allowance_amount(token_balance.toFixed(selected_token === "WETH" ? 4 : 2))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs bg-green-600 hover:bg-green-500 text-white rounded transition-colors"
                  >
                    Max
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Includes $2 buffer for future transactions
                </p>
              </div>

              <button
                onClick={handleApproval}
                disabled={
                  !allowance_amount ||
                  parseFloat(allowance_amount) < required_amount ||
                  approving
                }
                className="w-full px-6 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-700 disabled:text-gray-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {approving ? (
                  <>
                    <div className="w-4 h-4 border border-white/30 border-t-white rounded-full animate-spin"></div>
                    Approving {selected_token}...
                  </>
                ) : (
                  <>
                    Approve {allowance_amount} {selected_token}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {validation_status === "error" && (
          <div className="space-y-4 text-center">
            <div className="bg-red-500 rounded-full p-2 w-12 h-12 mx-auto flex items-center justify-center">
              <span className="text-white font-bold">!</span>
            </div>
            <p className="text-red-400 font-semibold">Validation Failed</p>
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
              <p className="text-red-300 text-sm">{temp.validation_error}</p>
            </div>
            <button
              onClick={checkBalanceAndAllowance}
              className="bg-red-600 hover:bg-red-500 text-white font-semibold px-6 py-2 rounded-lg transition-colors"
            >
              Retry Check
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Stage 4: Purchase Summary
const PurchaseSummary = () => {
  const {
    item,
    item_type,
    selected_token,
    temp,
    set_next_allowed,
  } = usePurchaseContext();
  const skm_con = useSkinsMasterContext();

  useEffect(() => {
    set_next_allowed(true);
  }, []);

  const get_token_price = () => {
    if (selected_token === "SGC") {
      return item_type === "pack" ? item.price_sgc : item.price_sgc;
    } else {
      const usd_price = item_type === "pack" ? item.price_usd : item.price_usd;
      return skm_con.usd_to_tok_val(usd_price, selected_token) || 0;
    }
  };

  const get_usd_value = () => {
    if (selected_token === "SGC") {
      return null; // No USD conversion for SGC
    } else {
      return item_type === "pack" ? item.price_usd : item.price_usd;
    }
  };

  const token_price = get_token_price();
  const usd_value = get_usd_value();

  return (
    <div className="p-6">
      <h3 className="text-xl font-semibold text-white mb-4 text-center">
        ⚠️ Purchase Confirmation
      </h3>

      <div className="max-w-md mx-auto space-y-6">
        <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
          <div className="flex items-center space-x-3 mb-3">
            <ShoppingCart className="w-6 h-6 text-blue-400" />
            <span className="text-blue-400 font-semibold">
              Purchase Summary
            </span>
          </div>
          <p className="text-gray-300 text-sm">
            Please review your purchase details before proceeding.
          </p>
        </div>

        <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
          <h4 className="text-white font-semibold mb-3">Purchase Details</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Type:</span>
              <span className="text-white">{item_type === "pack" ? "Skin Pack" : "Individual Skin"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Item:</span>
              <span className="text-white">{item?.name || "Unknown"}</span>
            </div>
            {item_type === "pack" && (
              <div className="flex justify-between">
                <span className="text-gray-400">Contains:</span>
                <span className="text-white">{item?.skinindi_ids?.length || 0} skins</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-400">Payment Method:</span>
              <div className="flex items-center space-x-2">
                <TokenIcon token={selected_token} size="xs" />
                <span className="text-white">{selected_token}</span>
              </div>
            </div>
            <div className="border-t border-gray-600 pt-2 mt-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Total Cost:</span>
                <div className="text-right">
                  <div className="text-white font-semibold">
                    {token_price.toFixed(selected_token === "WETH" ? 4 : 2)} {selected_token}
                  </div>
                  {usd_value && (
                    <div className="text-gray-400 text-xs">
                      ≈ ${usd_value.toFixed(2)} USD
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-orange-900/20 border border-orange-700/50 rounded-lg p-4">
          <div className="flex items-center space-x-3 mb-3">
            <AlertTriangle className="w-5 h-5 text-orange-400" />
            <span className="text-orange-400 font-semibold">Important Notes</span>
          </div>
          <ul className="text-sm text-gray-300 space-y-1">
            <li>• Purchase will be processed immediately</li>
            <li>• {item_type === "pack" ? "Pack contents" : "Skin"} will be added to your inventory</li>
            <li>• Transaction fees apply as per network conditions</li>
            <li>• This action cannot be reversed once confirmed</li>
          </ul>
        </div>

        <div className="text-center">
          <p className="text-gray-400 text-sm mb-4">
            Click "Purchase {item_type === "pack" ? "Pack" : "Skin"}" to proceed with the transaction.
          </p>
          <p className="text-blue-400 text-xs">
            Make sure you have enough {selected_token} for network fees.
          </p>
        </div>
      </div>
    </div>
  );
};

// Stage 5: Transaction Execution
const TransactionExecution = () => {
  const {
    item,
    item_type,
    selected_token,
    post_buy_function,
    set_next_allowed,
    set_step,
    temp,
    set_temp,
  } = usePurchaseContext();
  const aucon = useAuthContext();

  useEffect(() => {
    set_next_allowed(false);
  }, []);

  const executePurchase = async () => {
    try {
      set_temp((prev) => ({
        ...prev,
        tx_status: "processing",
        tx_error: "",
        tx_hash: "",
      }));

      // Get contract
      const ciparams = await aucon.get_contract_init_params();
      const con_store = await DNA_SkinsStore.get_contract(ciparams);

      // Execute purchase based on item type
      let tx;
      if (item_type === "pack") {
        tx = await con_store.run("buy_SkinPack", [item.id, selected_token], {
          wait: false,
        });
      } else {
        tx = await con_store.run("buy_Skin", [item.id, selected_token], {
          wait: false,
        });
      }

      const tx_hash = tx.hash;
      set_temp((prev) => ({ ...prev, tx_hash, tx_status: "confirming" }));

      // Wait for confirmation
      const receipt = await con_store.wait(tx);

      set_temp((prev) => ({
        ...prev,
        tx_status: "success",
        tx_receipt: receipt,
      }));
      set_next_allowed(true);

      // Call post_buy_function to refresh all data
      if (post_buy_function) {
        try {
          await post_buy_function();
        } catch (postBuyError) {
          console.warn("Failed to refresh UI data after purchase:", postBuyError);
        }
      }

      // Auto-advance to results
      setTimeout(() => {
        set_step(6);
      }, 2000);
    } catch (error) {
      console.error("Purchase transaction failed:", error);
      set_temp((prev) => ({
        ...prev,
        tx_status: "error",
        tx_error: error.message || "Transaction failed",
      }));
    }
  };

  const skm_con = useSkinsMasterContext();

  const get_token_price = () => {
    if (selected_token === "SGC") {
      return item_type === "pack" ? item.price_sgc : item.price_sgc;
    } else {
      const usd_price = item_type === "pack" ? item.price_usd : item.price_usd;
      return skm_con.usd_to_tok_val(usd_price, selected_token) || 0;
    }
  };

  return (
    <div className="p-6">
      <h3 className="text-xl font-semibold text-white mb-4 text-center">
        Purchase Transaction
      </h3>

      <div className="max-w-md mx-auto">
        {!temp.tx_status && (
          <div className="space-y-4 text-center">
            <div className="bg-blue-500 rounded-full p-4 w-20 h-20 mx-auto flex items-center justify-center">
              <ShoppingCart className="w-10 h-10 text-white" />
            </div>
            <p className="text-white font-semibold">Ready to Purchase</p>
            <p className="text-gray-400 text-sm mb-6">
              Click the button below to initiate the purchase transaction.
            </p>
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3 mb-4">
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-400">Cost:</span>
                  <span className="text-white">
                    {get_token_price().toFixed(selected_token === "WETH" ? 4 : 2)} {selected_token}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Item:</span>
                  <span className="text-white">{item?.name}</span>
                </div>
              </div>
            </div>
            <button
              onClick={executePurchase}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-bold px-8 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              🛒 Purchase {item_type === "pack" ? "Pack" : "Skin"}
            </button>
          </div>
        )}

        {temp.tx_status === "processing" && (
          <div className="space-y-4 text-center">
            <div className="animate-pulse w-12 h-12 bg-blue-500 rounded-full mx-auto flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-white animate-bounce" />
            </div>
            <p className="text-blue-400 font-semibold">Initiating Purchase...</p>
            <p className="text-gray-400 text-sm">
              Please confirm the transaction in your wallet
            </p>
          </div>
        )}

        {temp.tx_status === "confirming" && (
          <div className="space-y-4 text-center">
            <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="text-blue-400 font-semibold">Processing Purchase...</p>
            {temp.tx_hash && (
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
                <p className="text-gray-400 text-xs">Transaction Hash:</p>
                <p className="text-gray-300 text-sm font-mono break-all">
                  {temp.tx_hash}
                </p>
              </div>
            )}
            <p className="text-gray-400 text-sm">
              Please wait while we confirm the purchase transaction
            </p>
          </div>
        )}

        {temp.tx_status === "success" && (
          <div className="space-y-4 text-center">
            <div className="bg-green-500 rounded-full p-3 w-16 h-16 mx-auto flex items-center justify-center">
              <span className="text-white font-bold text-2xl">✓</span>
            </div>
            <p className="text-green-400 font-semibold text-lg">
              Purchase Successful!
            </p>
            <p className="text-gray-400 text-sm">Processing purchase results...</p>
          </div>
        )}

        {temp.tx_status === "error" && (
          <div className="space-y-4 text-center">
            <div className="bg-red-500 rounded-full p-3 w-16 h-16 mx-auto flex items-center justify-center">
              <span className="text-white font-bold text-xl">!</span>
            </div>
            <p className="text-red-400 font-semibold">Transaction Failed</p>
            {temp.tx_error && (
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
                <p className="text-gray-400 text-xs">Error:</p>
                <p className="text-red-300 text-sm">{temp.tx_error}</p>
              </div>
            )}
            <button
              onClick={executePurchase}
              className="bg-red-600 hover:bg-red-500 text-white font-semibold px-6 py-2 rounded-lg transition-colors"
            >
              Retry Purchase
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Stage 6: Purchase Results
const PurchaseResults = () => {
  const { item, item_type, temp, handle_close } = usePurchaseContext();
  const [purchase_event, set_purchase_event] = useState(null);

  useEffect(() => {
    if (temp.tx_receipt) {
      parsePurchaseEvent();
    }
  }, [temp.tx_receipt]);

  const parsePurchaseEvent = async () => {
    try {
      // Parse logs from transaction receipt
      const logs = temp.tx_receipt.parsed_logs || [];
      const event_name = item_type === "pack" ? "SkinPackPurchased" : "SkinPurchased";
      const purchase_event = logs.find((log) => log.name === event_name);

      if (purchase_event) {
        set_purchase_event(purchase_event.args);
      }
    } catch (error) {
      console.error("Failed to parse purchase event:", error);
    }
  };

  const handleViewTransaction = () => {
    if (temp.tx_hash) {
      window.open(polytxnlink(temp.tx_hash), "_blank");
    }
  };

  return (
    <div className="p-6">
      <h3 className="text-xl font-semibold text-white mb-4 text-center">
        🎉 Purchase Complete!
      </h3>

      <div className="max-w-md mx-auto space-y-6">
        <div className="text-center">
          <div className="bg-green-500 rounded-full p-4 w-20 h-20 mx-auto flex items-center justify-center mb-4">
            <ShoppingCart className="w-10 h-10 text-white" />
          </div>
          <p className="text-green-400 font-semibold text-lg mb-2">
            {item_type === "pack" ? "Pack" : "Skin"} Successfully Purchased!
          </p>
          <p className="text-gray-400 text-sm">
            Your {item_type === "pack" ? "pack" : "skin"} "{item?.name}" has been added to your inventory.
          </p>
        </div>

        {purchase_event && (
          <div className="bg-green-900/20 border border-green-700/50 rounded-lg p-4">
            <h4 className="text-green-400 font-semibold mb-3">Purchase Details</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Item ID:</span>
                <span className="text-green-400 font-bold">
                  {purchase_event.id}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Payment Token:</span>
                <span className="text-white">{purchase_event.token}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Amount Paid:</span>
                <span className="text-white">
                  {purchase_event.amt_tok?.toFixed(purchase_event.token === "WETH" ? 4 : 2)} {purchase_event.token}
                </span>
              </div>
              {purchase_event.amt_usd && (
                <div className="flex justify-between">
                  <span className="text-gray-400">USD Value:</span>
                  <span className="text-white">${purchase_event.amt_usd?.toFixed(2)}</span>
                </div>
              )}
              {item_type === "pack" && purchase_event.skins && (
                <div>
                  <span className="text-gray-400">Skins Received:</span>
                  <div className="mt-1 max-h-20 overflow-y-auto">
                    {purchase_event.skins.map((skin, index) => (
                      <div key={index} className="text-xs text-green-300">
                        • {skin.rvmode}-{skin.tokenid}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {temp.tx_hash && (
          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
            <h4 className="text-white font-semibold mb-2">
              Transaction Details
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Transaction:</span>
                <button
                  onClick={handleViewTransaction}
                  className="flex items-center space-x-1 text-blue-400 hover:text-blue-300 text-sm"
                >
                  <span>View on Chain</span>
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
              <p className="text-gray-300 text-xs font-mono break-all">
                {temp.tx_hash}
              </p>
            </div>
          </div>
        )}

        <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
          <h5 className="text-blue-400 font-medium mb-2">What's Next?</h5>
          <ul className="text-sm text-gray-300 space-y-1">
            <li>• Your {item_type === "pack" ? "pack contents have" : "skin has"} been added to "My Skins"</li>
            <li>• You can now apply {item_type === "pack" ? "any of the skins" : "the skin"} to your vehicles</li>
            <li>• Check your balance - it has been updated automatically</li>
            <li>• Transaction is permanently recorded on the blockchain</li>
          </ul>
        </div>

        <div className="text-center">
          <button
            onClick={handle_close}
            className="bg-purple-600 hover:bg-purple-500 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export const SkinStorePurchaseModal = ({
  trigger = <PurchaseButton />,
  item,
  item_type = "skin", // "skin" or "pack"
  post_buy_function,
  on_open,
  on_close,
}) => {
  const [open, set_open] = useState(false);
  const [step, set_step] = useState(1);
  const [next_allowed, set_next_allowed] = useState(false);
  const [temp, set_temp] = useState({});
  const [selected_token, set_selected_token] = useState("");

  const handle_open = () => {
    set_open(true);
    set_step(1);
    set_next_allowed(false);
    set_temp({});
    set_selected_token("");
    if (on_open) on_open();
  };

  const handle_close = () => {
    set_open(false);
    set_step(1);
    set_next_allowed(false);
    set_temp({});
    set_selected_token("");
    if (on_close) on_close();
  };

  const purchase_context = {
    step,
    set_step,
    next_allowed,
    set_next_allowed,
    handle_close,
    item,
    item_type,
    selected_token,
    set_selected_token,
    post_buy_function,
    temp,
    set_temp,
  };

  const render_current_stage = () => {
    switch (step) {
      case 1:
        return <AuthenticationCheck />;
      case 2:
        return <TokenSelection />;
      case 3:
        return <BalanceAllowanceValidation />;
      case 4:
        return <PurchaseSummary />;
      case 5:
        return <TransactionExecution />;
      case 6:
        return <PurchaseResults />;
      default:
        return <AuthenticationCheck />;
    }
  };

  const get_stage_title = () => {
    const titles = {
      1: "Authentication",
      2: "Payment Method",
      3: "Balance Check",
      4: "Purchase Summary",
      5: "Transaction",
      6: "Success",
    };
    return titles[step] || "Purchase";
  };

  return (
    <PurchaseContext.Provider value={purchase_context}>
      {/* Trigger Element */}
      <div onClick={handle_open} className="cursor-pointer">
        {trigger}
      </div>

      {/* Modal */}
      <Modal
        is_open={open}
        on_close={handle_close}
        title={`${get_stage_title()} - Step ${step} of 6`}
        size="lg"
        backdrop_close={step < 5} // Prevent close during transaction
        className="bg-gray-900/95 backdrop-blur-md"
      >
        {/* Main Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {render_current_stage()}
        </div>

        {/* Navigation Footer */}
        <div className="sticky bottom-0 border-t border-gray-800 p-6 flex justify-between bg-gray-900/95 backdrop-blur-md">
          <button
            onClick={() => {
              if (step > 1 && step < 5) {
                set_step(step - 1);
              }
            }}
            disabled={step === 1 || step >= 5}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              step === 1 || step >= 5
                ? "bg-gray-800 text-gray-500 cursor-not-allowed"
                : "bg-gray-700 text-white hover:bg-gray-600"
            }`}
          >
            Previous
          </button>

          <div className="text-gray-400 text-sm">Step {step} of 6</div>

          <button
            onClick={() => {
              if (step === 6) {
                handle_close();
              } else if (step === 5) {
                // Transaction stage - button handled by component
                return;
              } else if (next_allowed && step < 6) {
                set_step(step + 1);
              }
            }}
            disabled={!next_allowed && step < 5}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              step === 5
                ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                : step === 6
                  ? "bg-purple-600 text-white hover:bg-purple-500"
                  : next_allowed
                    ? "bg-blue-600 text-white hover:bg-blue-500"
                    : "bg-gray-600 text-gray-400 cursor-not-allowed"
            }`}
          >
            {step === 6 ? "Close" : step === 5 ? "Processing..." : "Next"}
          </button>
        </div>
      </Modal>
    </PurchaseContext.Provider>
  );
};